import {Component, DOCUMENT, Inject, inject, OnInit} from '@angular/core';
import {Router} from '@angular/router';
import {Location} from '@angular/common';
import {MatIconModule} from '@angular/material/icon';
import {MemoryService} from '../../../../core/services/memory.service';
import {LocationQueryDto} from '../../../startup/dtos/location-query.dto';
import {LocationDetailDto} from '../../../startup/dtos/location-detail.dto';
import {LocationService} from '../../../../core/services/location.service';
import {GroupSeriesInfoDto} from '../../dtos/groupseriesinfo.dto';
import {BreakpointObserver} from '@angular/cdk/layout';
import {LogoComponent} from '../../../../shared/components/logo/logo.component';
import {TextButton} from '../../../../shared/components/text-button/text-button.component';
import {TranslocoPipe, TranslocoService} from '@jsverse/transloco';
import {take} from 'rxjs';
import {MatFormFieldModule} from '@angular/material/form-field';
import {FormControl, ReactiveFormsModule} from '@angular/forms';
import {MatInputModule} from '@angular/material/input';
import {ErrorService} from '../../../../core/services/error.service';
import {CourseService} from '../../course.service';
import {BookingResults} from '../../models/bookingresult';
import {ConfirmResults} from '../../models/confirmresults';

@Component({
  selector: 'app-course-success',
  imports: [LogoComponent, MatIconModule, TextButton, TranslocoPipe, MatInputModule, MatFormFieldModule, MatFormFieldModule, ReactiveFormsModule],
  templateUrl: './course-success.component.html',
  styleUrl: './course-success.component.scss'
})
export class CourseSuccessComponent implements OnInit {
  router: Router = inject(Router);
  memoryService = inject(MemoryService);
  locationService = inject(LocationService);
  translocoService = inject(TranslocoService);
  errorService = inject(ErrorService);
  courseService = inject(CourseService);
  course!: GroupSeriesInfoDto;
  bookingResults: BookingResults[];
  succeededAppointments: BookingResults[];
  failedAppointments: BookingResults[];
  mode: 'mobile' | 'tablet' | 'desktop' = 'desktop';
  bookingMessageForm: FormControl = new FormControl("");
  title?: string;

  constructor(@Inject(DOCUMENT) private document: Document, private location: Location, private breakpointObserver: BreakpointObserver) {
    this.breakpointObserver.observe(['(max-width: 980px)',     // mobile
      '(min-width: 981px) and (max-width: 999px)', // tablet
      '(min-width: 1000px)'      // desktop
    ]).subscribe(result => {
      if (result.breakpoints['(max-width: 980px)']) {
        this.mode = 'mobile';
      } else if (result.breakpoints['(min-width: 981px) and (max-width: 999px)']) {
        this.mode = 'tablet';
      } else if (result.breakpoints['(min-width: 1000px)']) {
        this.mode = 'desktop';
      }
    });
    var data = this.location.getState() as ConfirmResults;
    this.course = data.course!;
    this.bookingResults = data.bookingResults!;
    this.succeededAppointments = this.bookingResults.filter(a => a.succeeded);
    this.failedAppointments = this.bookingResults.filter(a => !a.succeeded);
  }

  ngOnInit() {
    this.translocoService.selectTranslation().pipe(take(1)).subscribe(() => {
    });
    this.initializeLocation();
    if (this.failedAppointments.length == 0) {
      this.title = this.translocoService.translate('courses.success.title');
    } else if (this.succeededAppointments.length > 0) {
      this.title = this.translocoService.translate('courses.success.partly');
    } else {
      this.title = this.translocoService.translate('courses.success.failed');
    }
  }

  initializeLocation() {
    const locationId = this.memoryService.locationId;
    if (!locationId) {
      return;
    }

    const locationQueryDto: LocationQueryDto = {customerId: this.memoryService.customerId!, locationIds: [locationId]};
    this.locationService.queryLocationDetails(locationQueryDto).subscribe({
      next: (locationDetailDtos: LocationDetailDto[]) => {
        const locationDetailDto = locationDetailDtos.find(l => l.locationId == locationId);
        if (!locationDetailDto) {
          return;
        }

        this.setProperty("--custom-primary-color", locationDetailDto.appearanceSettings.primaryColor);
        this.setProperty("--custom-secondary-color", locationDetailDto.appearanceSettings.secondaryColor);
        this.setProperty("--custom-tertiary-color", locationDetailDto.appearanceSettings.tertiaryColor);
        this.memoryService.locationDetailDto = locationDetailDto;
      }
    });
  }

  locationInfo() {
    return this.memoryService.locationDetailDto?.name + ', ' + this.memoryService.locationDetailDto?.contact?.street + ', ' + this.memoryService.locationDetailDto?.contact?.city;
  }

  bookAppointment() {
    this.router.navigate(['/courses']);
  }

  manageAppointments() {
    this.router.navigate(['/profile/appointments']);
  }

  private setProperty(propertyName: string, value?: string) {
    if (!value) {
      return;
    }

    this.document.documentElement.style.setProperty(propertyName, value)
  }
}
